import { useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { useKitchensink } from '../context'
import { theme } from '../theme'

const sectionTitle: CSSProperties = {
  fontSize: '11px',
  fontWeight: 700,
  color: theme.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  margin: '0 0 16px',
}

const card: CSSProperties = {
  backgroundColor: theme.surface,
  border: `1px solid ${theme.border}`,
  borderRadius: '10px',
  padding: '20px 24px',
  marginBottom: '20px',
}

const buttonStyle: CSSProperties = {
  backgroundColor: theme.accent,
  color: '#fff',
  border: 'none',
  borderRadius: '7px',
  padding: '8px 18px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const code = (text: string) => (
  <code
    style={{
      fontFamily: 'ui-monospace, monospace',
      fontSize: '11px',
      color: theme.accent,
      backgroundColor: theme.accentSubtle,
      padding: '1px 5px',
      borderRadius: '3px',
    }}
  >
    {text}
  </code>
)

type StepProps = { n: number; children: ReactNode }

const Step = ({ n, children }: StepProps) => (
  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
    <div
      style={{
        flexShrink: 0,
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: theme.accentSubtle,
        border: `1px solid ${theme.accent}`,
        color: theme.accent,
        fontSize: '11px',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {n}
    </div>
    <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0, lineHeight: 1.6 }}>
      {children}
    </p>
  </div>
)

type LogEntry = {
  id: number
  label: string
  ts: string
}

export const EventQueuePage = () => {
  const { isInitialized } = useKitchensink()
  const [log, setLog] = useState<LogEntry[]>([])
  const [nextId, setNextId] = useState(1)

  const fire = (label: string, fn: () => void) => {
    fn()
    setLog(prev => [{ id: nextId, label, ts: new Date().toLocaleTimeString() }, ...prev])
    setNextId(n => n + 1)
  }

  const clearLog = () => setLog([])

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
        <span
          style={{
            fontSize: '10px',
            fontWeight: 700,
            color: theme.accent,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            backgroundColor: theme.accentSubtle,
            padding: '3px 8px',
            borderRadius: '4px',
          }}
        >
          Jitsu Queue
        </span>
      </div>

      <h1
        style={{
          fontSize: '24px',
          fontWeight: 700,
          color: theme.text,
          margin: '0 0 6px',
          letterSpacing: '-0.02em',
        }}
      >
        Event Queue
      </h1>
      <p style={{ fontSize: '13px', color: theme.textMuted, margin: '0 0 28px' }}>
        Verify that events fired before jitsu initializes are queued and replayed — not dropped.
        Fire events first, then initialize from the toolbar above.
      </p>

      {/* Init status banner */}
      <div
        style={{
          ...card,
          backgroundColor: isInitialized ? theme.successSubtle : theme.accentSubtle,
          border: `1px solid ${isInitialized ? 'rgba(34, 197, 94, 0.3)' : theme.accent}`,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
        }}
      >
        <span style={{ fontSize: '15px' }}>{isInitialized ? '✓' : '⏳'}</span>
        <span
          style={{
            fontSize: '13px',
            color: isInitialized ? theme.success : theme.accent,
            fontWeight: 500,
          }}
        >
          {isInitialized
            ? 'Snippet initialized — any queued events should have fired in the console'
            : 'Snippet not yet initialized — events fired below will be queued'}
        </span>
      </div>

      {/* Buttons */}
      <div style={card}>
        <p style={sectionTitle}>Fire events</p>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            data-test-id='queue-chat'
            style={buttonStyle}
            onClick={() =>
              fire('productImpression("Chat")', () =>
                window.ChiliWebData?.productImpression('Chat')
              )
            }
          >
            ProductImpression — Chat
          </button>
          <button
            data-test-id='queue-concierge'
            style={buttonStyle}
            onClick={() =>
              fire('productImpression("Concierge")', () =>
                window.ChiliWebData?.productImpression('Concierge')
              )
            }
          >
            ProductImpression — Concierge
          </button>
        </div>
      </div>

      {/* Log */}
      <div style={card}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
          }}
        >
          <p style={{ ...sectionTitle, margin: 0 }}>Fired events log</p>
          {log.length > 0 && (
            <button
              onClick={clearLog}
              style={{
                background: 'none',
                border: 'none',
                color: theme.textDim,
                fontSize: '12px',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Clear
            </button>
          )}
        </div>

        {log.length === 0 ? (
          <p style={{ fontSize: '13px', color: theme.textDim, margin: 0 }}>No events fired yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {log.map(entry => (
              <div
                key={entry.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '12px',
                  fontFamily: 'ui-monospace, monospace',
                  color: theme.text,
                  backgroundColor: theme.surfaceElevated,
                  padding: '6px 10px',
                  borderRadius: '6px',
                }}
              >
                <span style={{ color: theme.textDim }}>{entry.ts}</span>
                <span>{entry.label}</span>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: '10px',
                    color: isInitialized ? theme.success : theme.accent,
                    fontWeight: 600,
                  }}
                >
                  {isInitialized ? 'flushed' : 'queued'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* How to test */}
      <div style={card}>
        <p style={sectionTitle}>How to test</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Step n={1}>
            Make sure the snippet is <strong style={{ color: theme.text }}>not</strong> initialized
            yet. If it is, reload the page.
          </Step>
          <Step n={2}>Open browser DevTools → Console tab.</Step>
          <Step n={3}>
            Click <strong style={{ color: theme.text }}>ProductImpression — Chat</strong>. No
            console log should appear yet — the event is queued internally.
          </Step>
          <Step n={4}>
            Click <strong style={{ color: theme.text }}>ProductImpression — Concierge</strong>. Same
            — still queued.
          </Step>
          <Step n={5}>
            Go to the toolbar above, enter your Tenant Subdomain and Tenant ID, then click{' '}
            <strong style={{ color: theme.text }}>Initialize</strong>.
          </Step>
          <Step n={6}>
            Once initialized, verify the console shows both queued events replaying:{' '}
            {code("[jitsu] track ProductImpression { product: 'Chat' }")} followed by{' '}
            {code("[jitsu] track ProductImpression { product: 'Concierge' }")}.
          </Step>
          <Step n={7}>
            Fire another event after init. Verify it appears in the console immediately — no queue
            delay.
          </Step>
        </div>
      </div>
    </div>
  )
}
